import React from 'react'
import {
  UseFormRegister,
  UseFormHandleSubmit,
  useFieldArray,
  Control,
} from 'react-hook-form'
import {
  SlideInvestmentPlan,
  ProfitStructureItem,
  CashFlowItem,
  FinancialPlanItem,
  InvestmentPlanFormData,
} from '../hooks/investment_plan/types'
import { SlideContainer } from '../components/slide_container'
import { SlideHeader } from '../components/slide_header'
import {
  AiRegenerateSection,
  AiInstructionFormData,
} from '../components/ai_regenerate_section'
import FlatButton from '../../../components/buttons/flat_button'
import { TextInput } from '../../../components/form/inputs/TextInput'
import { TextareaInput } from '../../../components/form/inputs/TextareaInput'
import logoGray from '../../../images/logo_gray.png'

interface InvestmentPlanSlideProps {
  investmentPlan: SlideInvestmentPlan
  isEditing: boolean
  isUpdating: boolean
  isDirty: boolean
  register: UseFormRegister<InvestmentPlanFormData>
  control: Control<InvestmentPlanFormData>
  handleSubmit: UseFormHandleSubmit<InvestmentPlanFormData>
  registerAi: UseFormRegister<AiInstructionFormData>
  handleSubmitAi: UseFormHandleSubmit<AiInstructionFormData>
  aiInstruction: string
  onFormSubmit: (data: InvestmentPlanFormData) => Promise<void>
  onAiRegenerate: (data: AiInstructionFormData) => Promise<void>
}

export const InvestmentPlanSlide = React.forwardRef<
  HTMLDivElement,
  InvestmentPlanSlideProps
>(
  (
    {
      investmentPlan,
      isEditing,
      isUpdating,
      isDirty,
      register,
      control,
      handleSubmit,
      registerAi,
      handleSubmitAi,
      aiInstruction,
      onFormSubmit,
      onAiRegenerate,
    },
    ref
  ) => {
    const {
      fields: profitFields,
      append: appendProfit,
      remove: removeProfit,
    } = useFieldArray({
      control,
      name: 'profitStructureItems',
    })

    const {
      fields: cashFlowFields,
      append: appendCashFlow,
      remove: removeCashFlow,
    } = useFieldArray({
      control,
      name: 'cashFlowItems',
    })

    const {
      fields: financialFields,
      append: appendFinancial,
      remove: removeFinancial,
    } = useFieldArray({
      control,
      name: 'financialPlanItems',
    })
    return (
      <div ref={ref}>
        <SlideContainer>
          <SlideHeader
            title="3. 収益モデルと投資計画"
            isEditing={isEditing}
            isUpdating={isUpdating}
            isDirty={isDirty}
            onUpdate={handleSubmit(onFormSubmit)}
          />
          <div className="slide-content px-8 py-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-6">
              <div>
                <p className="text-lg font-bold mb-4">収益構造</p>
                {isEditing ? (
                  <TextareaInput
                    register={register('profitStructure')}
                    placeholder="収益構造の説明"
                    className="border-main-gray text-sm mb-4"
                  />
                ) : (
                  <p className="text-sm whitespace-pre-wrap mb-4">
                    {investmentPlan.profitStructure}
                  </p>
                )}
                <table className="w-full border border-main-darkgray">
                  <thead>
                    <tr className="bg-main-gray">
                      <th className="border-b border-r border-main-darkgray p-3 text-left text-sm font-bold">
                        収益項目
                      </th>
                      <th className="border-b border-r border-main-darkgray p-3 text-left text-sm font-bold">
                        予測値
                      </th>
                      {isEditing && (
                        <th className="border-b border-main-darkgray p-3 text-left text-sm font-bold">
                          削除
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {isEditing
                      ? profitFields.map((field, index) => (
                          <tr key={field.id}>
                            <td className="border-b border-r border-main-darkgray p-3">
                              <TextInput
                                register={register(
                                  `profitStructureItems.${index}.label` as const
                                )}
                                placeholder="収益項目名"
                              />
                            </td>
                            <td className="border-b border-r border-main-darkgray p-3">
                              <TextInput
                                register={register(
                                  `profitStructureItems.${index}.value` as const
                                )}
                                placeholder="予測値"
                              />
                            </td>
                            <td className="border-b border-main-darkgray p-3 text-center">
                              <FlatButton
                                type="button"
                                onClick={() => removeProfit(index)}
                                disabled={
                                  isUpdating || profitFields.length <= 1
                                }
                                className="!bg-main-red w-full"
                              >
                                削除
                              </FlatButton>
                            </td>
                          </tr>
                        ))
                      : (() => {
                          try {
                            const items = JSON.parse(
                              investmentPlan.profitStructureItems || '[]'
                            ) as ProfitStructureItem[]
                            return items.map((item, index) => (
                              <tr key={index}>
                                <td className="border-b border-r border-main-darkgray p-3 text-sm">
                                  {item.label}
                                </td>
                                <td className="border-b border-main-darkgray p-3 text-sm">
                                  {item.value}
                                </td>
                              </tr>
                            ))
                          } catch (e) {
                            return (
                              <tr>
                                <td
                                  colSpan={2}
                                  className="p-3 text-sm text-center"
                                >
                                  データの読み込みに失敗しました
                                </td>
                              </tr>
                            )
                          }
                        })()}
                  </tbody>
                </table>
                {isEditing && (
                  <div className="mt-2">
                    <FlatButton
                      type="button"
                      onClick={() => appendProfit({ label: '', value: '' })}
                      disabled={isUpdating}
                    >
                      行を追加
                    </FlatButton>
                  </div>
                )}
              </div>
              <div>
                <p className="text-lg font-bold mb-4">初期投資と財務計画</p>
                {isEditing ? (
                  <TextareaInput
                    register={register('financialPlan')}
                    placeholder="初期投資と財務計画の説明"
                    className="border-main-gray text-sm mb-4"
                  />
                ) : (
                  <p className="text-sm whitespace-pre-wrap mb-4">
                    {investmentPlan.financialPlan}
                  </p>
                )}
                <table className="w-full border border-main-darkgray">
                  <thead>
                    <tr className="bg-main-gray">
                      <th className="border-b border-r border-main-darkgray p-3 text-left text-sm font-bold">
                        費用項目
                      </th>
                      <th className="border-b border-r border-main-darkgray p-3 text-left text-sm font-bold">
                        投資額
                      </th>
                      {isEditing && (
                        <th className="border-b border-main-darkgray p-3 text-left text-sm font-bold">
                          削除
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {isEditing
                      ? financialFields.map((field, index) => (
                          <tr key={field.id}>
                            <td className="border-b border-r border-main-darkgray p-3">
                              <TextInput
                                register={register(
                                  `financialPlanItems.${index}.label` as const
                                )}
                                placeholder="費用項目"
                              />
                            </td>
                            <td className="border-b border-r border-main-darkgray p-3">
                              <TextInput
                                register={register(
                                  `financialPlanItems.${index}.value` as const
                                )}
                                placeholder="投資額"
                              />
                            </td>
                            <td className="border-b border-main-darkgray p-3">
                              <FlatButton
                                type="button"
                                onClick={() => removeFinancial(index)}
                                disabled={
                                  isUpdating || financialFields.length <= 1
                                }
                                className="!bg-main-red w-full"
                              >
                                削除
                              </FlatButton>
                            </td>
                          </tr>
                        ))
                      : (() => {
                          try {
                            const items = JSON.parse(
                              investmentPlan.financialPlanItems || '[]'
                            ) as FinancialPlanItem[]
                            return items.map((item, index) => (
                              <tr key={index}>
                                <td className="border-b border-r border-main-darkgray p-3 text-sm">
                                  {item.label}
                                </td>
                                <td className="border-b border-main-darkgray p-3 text-sm">
                                  {item.value}
                                </td>
                              </tr>
                            ))
                          } catch (e) {
                            return (
                              <tr>
                                <td
                                  colSpan={2}
                                  className="p-3 text-sm text-center"
                                >
                                  データの読み込みに失敗しました
                                </td>
                              </tr>
                            )
                          }
                        })()}
                  </tbody>
                </table>
                {isEditing && (
                  <div className="mt-2">
                    <FlatButton
                      type="button"
                      onClick={() => appendFinancial({ label: '', value: '' })}
                      disabled={isUpdating}
                    >
                      行を追加
                    </FlatButton>
                  </div>
                )}
              </div>
            </div>
            <div className="mb-6">
              <p className="text-lg font-bold mb-4">キャッシュフロー計画</p>
              {isEditing ? (
                <TextareaInput
                  register={register('cashFlow')}
                  placeholder="キャッシュフロー計画の説明"
                  className="border-main-gray text-sm mb-4"
                />
              ) : (
                <p className="text-sm whitespace-pre-wrap mb-4">
                  {investmentPlan.cashFlow}
                </p>
              )}
              <div className="overflow-x-auto">
                <table className="w-full border border-main-darkgray">
                  <thead>
                    <tr className="bg-main-gray">
                      <th className="border-b border-r border-main-darkgray p-3 text-left text-sm font-bold">
                        年度
                      </th>
                      <th className="border-b border-r border-main-darkgray p-3 text-left text-sm font-bold">
                        売上
                      </th>
                      <th className="border-b border-r border-main-darkgray p-3 text-left text-sm font-bold">
                        営業利益
                      </th>
                      <th className="border-b border-r border-main-darkgray p-3 text-left text-sm font-bold">
                        収益回収率
                      </th>
                      {isEditing && (
                        <th className="border-b border-main-darkgray p-3 text-left text-sm font-bold">
                          削除
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {isEditing
                      ? cashFlowFields.map((field, index) => (
                          <tr key={field.id}>
                            <td className="border-b border-r border-main-darkgray p-3">
                              <TextInput
                                register={register(
                                  `cashFlowItems.${index}.year` as const
                                )}
                                placeholder="年度"
                              />
                            </td>
                            <td className="border-b border-r border-main-darkgray p-3">
                              <TextInput
                                register={register(
                                  `cashFlowItems.${index}.sales` as const
                                )}
                                placeholder="売上"
                              />
                            </td>
                            <td className="border-b border-r border-main-darkgray p-3">
                              <TextInput
                                register={register(
                                  `cashFlowItems.${index}.operatingIncome` as const
                                )}
                                placeholder="営業利益"
                              />
                            </td>
                            <td className="border-b border-r border-main-darkgray p-3">
                              <TextInput
                                register={register(
                                  `cashFlowItems.${index}.recovery` as const
                                )}
                                placeholder="収益回収率"
                              />
                            </td>
                            <td className="border-b border-main-darkgray p-3">
                              <FlatButton
                                type="button"
                                onClick={() => removeCashFlow(index)}
                                className="!bg-main-red w-full"
                                disabled={
                                  isUpdating || cashFlowFields.length <= 1
                                }
                              >
                                削除
                              </FlatButton>
                            </td>
                          </tr>
                        ))
                      : (() => {
                          try {
                            const items = JSON.parse(
                              investmentPlan.cashFlowItems || '[]'
                            ) as CashFlowItem[]
                            return items.map((item, index) => (
                              <tr key={index}>
                                <td className="border-b border-r border-main-darkgray p-3 text-sm">
                                  {item.year}
                                </td>
                                <td className="border-b border-r border-main-darkgray p-3 text-sm">
                                  {item.sales}
                                </td>
                                <td className="border-b border-r border-main-darkgray p-3 text-sm">
                                  {item.operatingIncome}
                                </td>
                                <td className="border-b border-main-darkgray p-3 text-sm">
                                  {item.recovery}
                                </td>
                              </tr>
                            ))
                          } catch (e) {
                            return (
                              <tr>
                                <td
                                  colSpan={4}
                                  className="p-3 text-sm text-center"
                                >
                                  データの読み込みに失敗しました
                                </td>
                              </tr>
                            )
                          }
                        })()}
                  </tbody>
                </table>
              </div>
              {isEditing && (
                <div className="mt-2">
                  <FlatButton
                    type="button"
                    onClick={() =>
                      appendCashFlow({
                        year: '',
                        sales: '',
                        operatingIncome: '',
                        recovery: '',
                      })
                    }
                    disabled={isUpdating}
                  >
                    行を追加
                  </FlatButton>
                </div>
              )}
            </div>

            {isEditing && (
              <AiRegenerateSection
                register={registerAi}
                handleSubmit={handleSubmitAi}
                aiInstruction={aiInstruction}
                isUpdating={isUpdating}
                onSubmit={onAiRegenerate}
              />
            )}
            <div className="slide-footer mt-20">
              <p className="text-xs text-main-darkgray text-center mb-2">
                この企画書は「SDGs 共創
                Lodge」の共創アイディア生成AIで生成しました。
              </p>
              <img
                src={logoGray}
                alt="SDGs共創Lodge Logo"
                className="absolute bottom-2 right-4 w-20 h-auto"
              />
            </div>
          </div>
        </SlideContainer>
      </div>
    )
  }
)

InvestmentPlanSlide.displayName = 'InvestmentPlanSlide'
